import { ActionReducerMapBuilder, createAsyncThunk } from "@reduxjs/toolkit"
import { ProductFields } from "@tenforce/product-fields"

import { GlobalState } from "../../../../store"
import {
    addInlineAttachmentDataApi,
    getAttachmentCreatorDataApi,
    removeInlineAttachmentDataApi,
    updateAttachmentValueDataApi,
} from "../../../../apis/attachments"
import { addErrorNotification } from "../../../notifications/actions"
import { IInlineAttachmentField } from "../../"
import { fetchFields, handleFieldUpdateFeedback } from "../../slice"
import {
    AttachmentDTO,
    AttachmentCreatorDTO,
} from "../../../../apis/models/attachments"
import { IItem } from "../../../../models"
import { formatString } from "../../../../util/Strings"
import { Exceptions } from "../../../../util/Translations"

export const {
    AnErrorOccurredWhileAddingAttachmentTo = 'An error occurred while adding the attachment ["{0}"] to ["{1}"].',
    AnErrorOccurredWhileFetchingAttachmentCreator = 'An error occurred while loading the creator of ["{0}"].',
    AnErrorOccurredWhileRemovingAttachmentFrom = 'An error occurred while removing an attachment from ["{0}"].',
    AnErrorOccurredWhileUpdatingThisField = 'An error occurred while updating this field: ["{0}"].',
} = Exceptions

interface AttachmentParameters {
    field: IInlineAttachmentField
    files: File[]
}

interface UpdateAttachment {
    field: IInlineAttachmentField
    newValue: Partial<AttachmentDTO>
    attachment?: ProductFields.Attachment
    unknownCreator?: string
}

interface RemoveAttachment {
    field: IInlineAttachmentField
    attachment?: ProductFields.Attachment
}

export const attachmentsReducersBuilder = (
    builder: ActionReducerMapBuilder<IItem>,
): void => {
    builder
        .addCase(addAttachment.pending, (state: IItem) => {
            state.isLoading = true
        })
        .addCase(addAttachment.fulfilled, (state: IItem) => {
            state.isLoading = false
        })
        .addCase(addAttachment.rejected, (state: IItem) => {
            state.isLoading = false
        })

    builder.addCase(addCreatorToAttachment.fulfilled, (state, action) => {
        if (!action.payload) {
            return
        }
        const { field, attachment } = action.payload
        if (!attachment) {
            return
        }
        const currentAttachments: ProductFields.Attachment[] = state?.fields?.[
            field.fieldId
        ]?.value as ProductFields.Attachment[]

        if (!currentAttachments || currentAttachments.length === 0) {
            return
        }

        const index = currentAttachments.findIndex(
            (a: ProductFields.Attachment) => a.id === attachment.id,
        )
        currentAttachments[index] = attachment
        if (state?.fields?.[field.fieldId]) {
            state.fields[field.fieldId].value = currentAttachments
        }
    })

    builder
        .addCase(removeAttachment.pending, (state: IItem) => {
            state.isLoading = true
        })
        .addCase(removeAttachment.fulfilled, (state: IItem) => {
            state.isLoading = false
        })
        .addCase(removeAttachment.rejected, (state: IItem) => {
            state.isLoading = false
        })

    builder
        .addCase(updateAttachment.pending, (state: IItem) => {
            state.isLoading = true
        })
        .addCase(updateAttachment.fulfilled, (state: IItem) => {
            state.isLoading = false
        })
        .addCase(updateAttachment.rejected, (state: IItem) => {
            state.isLoading = false
        })
}

export async function getCreatorLabel(
    attachmentIds: number[],
): Promise<PromiseSettledResult<Partial<AttachmentCreatorDTO>>[]> {
    const promises: Promise<Partial<AttachmentCreatorDTO>>[] = []
    attachmentIds.map((attachmentId) => {
        promises.push(
            getAttachmentCreatorDataApi(attachmentId, {
                $select: "Label",
            }),
        )
    })
    return await Promise.allSettled(promises)
}

export const addCreatorToAttachment = createAsyncThunk(
    "fields/inlineAttachment/addCreatorToAttachment",
    async (
        {
            field,
            attachment,
            unknownCreator,
        }: {
            field: IInlineAttachmentField
            attachment: ProductFields.Attachment
            unknownCreator?: string
        },
        { dispatch },
    ) => {
        if (!attachment) {
            return
        }
        let creator: string | null | undefined = ""
        try {
            creator = (
                await getAttachmentCreatorDataApi(attachment.id, {
                    $select: "Label",
                })
            ).Label
        } catch (error) {
            const errorMessage = formatString(
                AnErrorOccurredWhileFetchingAttachmentCreator,
                attachment.fileName,
            )
            console.error(`${errorMessage} `, error)
            dispatch(addErrorNotification(errorMessage))
        }
        const updatedAttachment = {
            ...attachment,
            creator: creator || unknownCreator,
        }
        return { field, attachment: updatedAttachment }
    },
)

export const updateAttachment = createAsyncThunk(
    "fields/inlineAttachment/updateAttachment",
    async (
        { field, newValue, attachment, unknownCreator }: UpdateAttachment,
        { getState, dispatch },
    ) => {
        if (!attachment) {
            return
        }
        const state = getState() as GlobalState
        const { feedbackKeys } = state.fields

        try {
            await updateAttachmentValueDataApi(attachment.id, {
                ...newValue,
            })
        } catch (error) {
            let errorMessage = formatString(
                AnErrorOccurredWhileUpdatingThisField,
                field.title,
            )
            console.error(`${errorMessage} (${field.fieldId}): `, error)
            if (error instanceof Error) {
                errorMessage += ` ${error.message}`
            }
            console.error(errorMessage)
            dispatch(addErrorNotification(errorMessage))
        } finally {
            await dispatch(fetchFields())
            if (feedbackKeys) {
                handleFieldUpdateFeedback(feedbackKeys, field, dispatch)
            }
            dispatch(
                addCreatorToAttachment({
                    field,
                    attachment: attachment,
                    unknownCreator: unknownCreator,
                }),
            )
        }
    },
)

export const removeAttachment = createAsyncThunk(
    "fields/inlineAttachment/removeAttachment",
    async ({ field, attachment }: RemoveAttachment, { dispatch }) => {
        if (!attachment) {
            return
        }
        try {
            await removeInlineAttachmentDataApi(
                field.itemId,
                field.fieldId,
                attachment.id,
            )
        } catch (error) {
            let errorMessage = formatString(
                AnErrorOccurredWhileRemovingAttachmentFrom,
                field.title,
            )
            console.error(`${errorMessage} (${field.fieldId}): `, error)
            if (error instanceof Error) {
                errorMessage += ` ${error.message}`
            }
            dispatch(addErrorNotification(errorMessage))
        } finally {
            await dispatch(fetchFields())
        }
    },
)

export const addAttachment = createAsyncThunk(
    "fields/inlineAttachment/addAttachment",
    async ({ files, field }: AttachmentParameters, { dispatch }) => {
        const promises: Promise<AttachmentDTO>[] = []
        files.map((file) => {
            promises.push(
                addInlineAttachmentDataApi(field.itemId, field.fieldId, file),
            )
        })
        try {
            const settledPromises: PromiseSettledResult<AttachmentDTO>[] =
                await Promise.allSettled(promises)
            settledPromises.map(async (result, index) => {
                if (result.status !== "fulfilled") {
                    const error = result.reason
                    const fileName = files[index].name
                    let errorMessage = formatString(
                        AnErrorOccurredWhileAddingAttachmentTo,
                        fileName,
                        field.title,
                    )
                    console.error(`${errorMessage} (${field.fieldId}): `, error)
                    if (error instanceof Error) {
                        errorMessage += `\n${error.message}`
                    }
                    dispatch(addErrorNotification(errorMessage))
                }
            })
        } catch (error) {
            let errorMessage = formatString(
                AnErrorOccurredWhileAddingAttachmentTo,
                field.title,
            )
            console.error(`${errorMessage} (${field.fieldId}): `, error)
            if (error instanceof Error) {
                errorMessage += `\n${error.message}`
            }
            dispatch(addErrorNotification(errorMessage))
        } finally {
            await dispatch(fetchFields())
        }
    },
    {
        condition: (_, { getState }) => {
            const { fields } = getState() as GlobalState
            if (fields.id === -1 || fields.isFetchingFields) {
                // no react fields are present or
                // fetching is in progress
                return false
            }
        },
    },
)
